import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { freight } from "../lib/freightApi.js";
import { StaffShell, Panel, Button, Input, TextArea, Note, Micro, T, errText, mailError, fmtDate } from "./ui.jsx";

/**
 * Our admin: the setup email every new carrier gets (portal plan slice 8; the schematic's Setup
 * email board). Brian, 2026-10-30: "This is the email template that we will send them initially.
 * It doesn't live anywhere on the client's dashboard."
 *
 * Only the wrapping is edited here: the subject, the opening and the sign-off. The questions are
 * the catalog's, the same list each carrier's setup page tracks, so the two cannot drift apart.
 * {carrier} and {staffName} are filled in when it is sent.
 */
export function TemplateEditor({ template, onSaved }) {
  const [subject, setSubject] = useState(template.subject);
  const [intro, setIntro] = useState(template.intro);
  const [closing, setClosing] = useState(template.closing);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); setMsg(null); try { await fn(); } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); } };
  return (
    <Panel id="template-title" title="The wrapping"
      note={template.isDefault ? "The default. Edit it and it becomes the default for every new carrier." : `Last edited by ${template.updatedBy}, ${fmtDate(template.updatedAt)}.`}>
      <div style={{ display: "grid", gap: 12 }}>
        <Input label="Subject" value={subject} onChange={setSubject} />
        <TextArea label="Opening, after “Hi [first name],”" value={intro} onChange={setIntro} rows={4} />
        <TextArea label="Sign-off" value={closing} onChange={setClosing} rows={3} />
        <div style={{ fontSize: 12.5, color: T.ink3 }}>{"{carrier}"} becomes the carrier’s name and {"{staffName}"} the sender’s first name. A field left empty goes back to the default.</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button size="sm" disabled={busy} onClick={() => run(async () => { onSaved(await freight.staff.saveTemplate({ subject, intro, closing })); setMsg({ ok: true, text: "Saved." }); })}>Save</Button>
          <Button size="sm" kind="quiet" disabled={busy} onClick={() => run(async () => {
            const e = await freight.staff.sendTest("full");
            setMsg(e.sent ? { ok: true, text: `A test went to ${e.to}.` } : { ok: false, text: `The test was not sent: ${mailError(e.error)}.` });
          })}>Send a test to me</Button>
        </div>
        <Note msg={msg} />
      </div>
    </Panel>
  );
}

export function QuestionList({ sections }) {
  return (
    <Panel id="questions-title" title="What it asks" note={`${sections.reduce((n, s) => n + s.questions.length, 0)} questions in ${sections.length} sections, the same list each carrier’s setup page tracks. Attached: the drivers sheet and the office people sheet, both CSV.`}>
      <div style={{ display: "grid", gap: 14 }}>
        {sections.map((s, i) => (
          <section key={s.id} aria-labelledby={`s-${s.id}`}>
            <h3 id={`s-${s.id}`} style={{ margin: "0 0 4px", fontSize: 14.5, fontWeight: 600 }}>{i + 1}. {s.title}</h3>
            {s.intro && <p style={{ margin: "0 0 4px", fontSize: 13.5 }}>{s.intro}</p>}
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, lineHeight: 1.5 }}>
              {(s.defaults && s.defaults.length ? s.defaults : s.questions.map((q) => q.ask)).map((t) => <li key={t}>{t}</li>)}
              {(s.notes || []).map((t) => <li key={t} style={{ color: T.ink2 }}>{t}</li>)}
            </ul>
          </section>
        ))}
      </div>
    </Panel>
  );
}

export default function StaffSetupEmail() {
  const [template, setTemplate] = useState(null);
  const [sections, setSections] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    Promise.all([freight.staff.template(), freight.staff.catalog()])
      .then(([t, s]) => { if (alive) { setTemplate(t); setSections(s); } })
      .catch((e) => alive && setErr(errText(e)));
    return () => { alive = false; };
  }, []);
  return (
    <StaffShell active="email">
      <Link to="/staff" className="stf-link" style={{ fontSize: 13.5, color: T.ink2 }}>← Carriers</Link>
      <div>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, letterSpacing: -0.7 }}>Setup email</h1>
        <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 6 }}>The default for every new carrier: everything we need, in one reply.</div>
      </div>
      <Note msg={err ? { ok: false, text: err } : null} />
      {!err && (!template || !sections) && <div style={{ color: T.ink3, fontSize: 13.5 }}>Loading…</div>}
      {template && <TemplateEditor template={template} onSaved={setTemplate} />}
      {sections && <QuestionList sections={sections} />}
      <Micro style={{ marginTop: 4 }}>Replies come to whoever sends the email. Key the answers in on the carrier’s page.</Micro>
    </StaffShell>
  );
}
